import { useCallback, useMemo } from 'react';
import { pruneNilValues } from '@core/object';
import { CLICK_ADD_TO_CART, CLICK_PRODUCT_CARD, } from '@core/shopping/tracking/endpoints';
import { ICommerceClickTarget, ICommerceInitialClickTrigger, } from '@core/shopping/tracking/types';
import { getClickTriggerPixelValue } from '@core/shopping/tracking/utils';
import { createCommerceTrackingData } from '@core/shopping/utils/createCommerceTrackingData';
import { sendPixelTrackingWithSession } from '@core/tracking';
import { getVideoPlayId } from '@core/tracking/videoPlayIdTracking';
import { PlaySourceEnum } from '@embed/constants';
import { ENGAGEMENT_INTERACTABLE_VIEW, ENGAGEMENT_INTERACTABLE_VIEW_V2, } from './endpoints/engagement';
import { useTracking } from './useTracking';
/**
 * Hook that provides a callback for tracking of CTA Impression pixel event in Player Deck presentation.
 */
export const useTrackPlayerDeckProductCardClick = (video) => {
    const { params, pixelTrackingData } = useTracking();
    const widgetId = params?.widget_id ?? '';
    const videoPlayId = getVideoPlayId(widgetId, video?.encoded_id ?? '', PlaySourceEnum.AUTOPLAY);
    const makePixelData = useCallback((product, clickTarget, initialClickTrigger) => ({
        event_properties: pruneNilValues({
            ...pixelTrackingData,
            variant: video?.variant,
            _video_id: video?.encoded_id,
            ...createCommerceTrackingData({
                product,
                clickTarget,
            }),
            click_trigger: getClickTriggerPixelValue(initialClickTrigger),
            presentation: 'player_deck',
            video_play_id: videoPlayId,
        }),
    }), [pixelTrackingData, video, videoPlayId]);
    return useMemo(() => ({
        trackProductCardClick: (product) => {
            const pixelData = makePixelData(product, ICommerceClickTarget.OUTSTREAM_PDP, ICommerceInitialClickTrigger.PRODUCT_CARD);
            sendPixelTrackingWithSession({
                host: params?.api_host_tracking,
                endpoint: CLICK_PRODUCT_CARD,
                data: pixelData,
            });
            // Tracking engagement on product card click
            sendPixelTrackingWithSession({
                host: params?.api_host_tracking,
                endpoint: ENGAGEMENT_INTERACTABLE_VIEW,
                data: pixelData,
            });
            sendPixelTrackingWithSession({
                host: params?.api_host_tracking,
                endpoint: ENGAGEMENT_INTERACTABLE_VIEW_V2,
                data: pixelData,
            });
        },
        trackAddToCartClick: (product) => {
            const pixelData = makePixelData(product, ICommerceClickTarget.OUTSTREAM_PDP, ICommerceInitialClickTrigger.PRODUCT_CARD);
            sendPixelTrackingWithSession({
                host: params?.api_host_tracking,
                endpoint: CLICK_ADD_TO_CART,
                data: pixelData,
            });
        },
    }), [params?.api_host_tracking, makePixelData]);
};
